import startOfDay from 'date-fns/startOfDay'
import * as React from 'react'
import { useHistory } from 'react-router'
import * as VariantActions from '../../../../actions/variants'
import { VariantCreateAction, VariantCreation } from '../../../../actions/variants/createVariant'
import { VariantUpdateAction } from '../../../../actions/variants/updateVariant'
import { useAppDispatch } from '../../../../actions/types'
import { Routes } from '../../../../constants'
import type { Variant } from '../../../../types'
import type { VariantFormData } from '../types'
import * as Validation from '../validation'
import useVariantParams from '../useVariantParams'

export default function useVariantFormData(variant?: Variant) {
    const dispatch = useAppDispatch()
    const history = useHistory()
    const { estate } = useVariantParams()
    const [variantData, setVariantData] = React.useState<VariantFormData>({
        class: 'sale-sku',
        priority: 1,
        brand: null,
        start_date: startOfDay(new Date()),
        end_date: startOfDay(new Date(Date.now() + 1000 * 60 * 60 * 24 * 7)),
        skus: [],
        exceptions: {
            regions: [],
            skus: [],
            price: {
                less_than: null,
                greater_than: null,
            },
            stores: [],
            uids: [],
            dates: [],
        },
    })
    const [saveProgress, setSaveProgress] = React.useState<undefined | number>()
    const [areUnsavedChanges, setUnsavedChanges] = React.useState(false)
    const [formErrors, setFormErrors] = React.useState<Validation.VariantValidation>({})
    const [submitError, setSubmitErrors] = React.useState<Error>()

    React.useEffect(() => {
        const isNew = !variant

        if (isNew) {
            setVariantData(prev => ({
                ...prev,
                class: 'sale-sku',
            }))
        } else {
            setVariantData(prev => ({
                ...prev,
                ...variant,
                exceptions: {
                    regions: [],
                    skus: [],
                    price: { less_than: null, greater_than: null },
                    stores: [],
                    uids: [],
                    dates: [],
                    ...variant?.exceptions,
                },
            }))
        }
    }, [variant])

    const isNew = !variant

    const thereAreErrors =
        (validation: Validation.VariantValidation) => Object.keys(validation).length > 0

    const createVariant = React.useCallback(async (variantData: VariantFormData) => {
        setSubmitErrors(undefined)
        setSaveProgress(0)

        const createAction: VariantCreateAction = await dispatch(
            VariantActions.createVariant(variantData as VariantCreation)
        )

        if (createAction.type !== 'VARIANT_CREATE##SUCCESS') {
            setSubmitErrors(createAction.payload)
            setSaveProgress(undefined)
            return
        }

        const { id: variantId } = createAction.payload
        setSaveProgress(1)

        history.replace(
            Routes.buildRoute(
                Routes.SELF_SERVE_VARIANTS,
                { estate, variant: variantId },
            )
        )
    }, [dispatch, estate, history])

    const updateVariant = React.useCallback(async (variantData: VariantFormData) => {
        setSubmitErrors(undefined)
        setSaveProgress(0)

        const updateAction: VariantUpdateAction = await dispatch(
            VariantActions.updateVariant(variantData as Variant)
        )

        if (updateAction.type !== 'VARIANT_UPDATE##SUCCESS') {
            setSubmitErrors(updateAction.payload)
            setSaveProgress(undefined)
            return
        }

        setSaveProgress(1)
    }, [dispatch])

    const onSubmit = React.useCallback(() => {
        setFormErrors({})
        setUnsavedChanges(false)

        const errors = isNew
            ? Validation.validateCreation(variantData)
            : Validation.validateUpdate(variantData)

        if (thereAreErrors(errors)) {
            setFormErrors(errors)
            return
        }

        const saveAction = isNew ? createVariant : updateVariant

        saveAction(variantData).then(
            () => setTimeout(() => setSaveProgress(undefined), 5000),
        )
    }, [createVariant, isNew, variantData, updateVariant])

    return {
        areUnsavedChanges,
        formErrors,
        onSubmit,
        variantData,
        saveProgress,
        setFormErrors,
        setSubmitErrors,
        setVariantData,
        setUnsavedChanges,
        submitError,
    }
}
